import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { SHAPE_FACTSHEET_BLUEPRINT } from '../../shapes'
import ComponentLoader from '../component-loader'
import { getComponentLabel } from '../../helper'
import { Trans, withTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import activityTrackingService from '../../../../../services/activityTrackingService'
import { factsheetActions } from '../../../../../redux/actions/factsheet'
import { trackingEvent } from '../../../../../constants'
import debounce from 'lodash.debounce'
import SearchField from '../../../SearchField'
import './styles.scss'
import { environment } from '../../../../../environment'
import PageHead from '../../../PageHead'
import Toggle from '../../../Toggle'
import ErroneousFactsNoticeOverlay from './components/ErroneousFactsNoticeOverlay'

class SectionGroupContainer extends Component {
  // used to scroll the factsheet into view when a search is triggered from
  // outside (e.g. clicking a contradicting fact in the notification center).
  factsheetHeaderRef = React.createRef()
  // wraps the selected group's facts; scoped lookup target when jumping to a
  // specific fact so we never match a duplicate rendered elsewhere.
  factsContainerRef = React.createRef()
  MIN_SEARCH_TEXT_LENGTH = 3
  SEARCHABLE_FACT_TYPES = new Set(['fact:checkbox', 'fact:single-attribute-controlled'])
  SECTION_GROUP_GROUPS = new Map([
    // a map of the section group component ID to a group index value.
    // the ones found while parsing the blueprint will be rendered in the specified group.
    // any not found will be rendered in one new group at the end.
    ['16', 0],
    ['5', 0],
    ['3', 0],
    ['9', 0],

    ['2', 1],
    ['10', 1],
    ['11', 1],

    ['6', 2],
    ['8', 2],
    ['54', 2],
    ['45', 2],
    ['7', 2],
    ['52', 2]
  ])
  state = {
    groupComponentKeys: {},
    selectedGroupKey: null,
    hasGroupedComponents: false,
    showGroupNavigationNotAllowedModal: false,
    searchValue: '',
    filteredComponents: [],
    // a map of render group index value to a list of component indices.
    renderGroups: null,
    // a list of the actual indices in the render group map, sorted in ascending order.
    // here to avoid sorting everytime.
    renderGroupsKeys: [],
    showSelectedFactsOnly: false,
    filteringInProgress: false,
  }

  componentDidMount () {
    this.onBlueprintChange()
    this.applyPendingFactsheetScroll()
    this.applyPendingFactFocus()
  }

  componentDidUpdate (prevProps) {
    if (
      (prevProps.blueprint !== this.props.blueprint)
      || (prevProps.factsheetBlueprint !== this.props.factsheetBlueprint)
    ) {
      this.onBlueprintChange()
    }
    if (this.props.pendingFactsheetScroll && !prevProps.pendingFactsheetScroll) {
      this.applyPendingFactsheetScroll()
    }
    if (this.props.pendingFactFocus && prevProps.pendingFactFocus !== this.props.pendingFactFocus) {
      this.applyPendingFactFocus()
    }
  }

  // runs the factsheet search for a term and scrolls the factsheet into view.
  runFactSearch = (term) => {
    const searchValue = term.trimStart()
    this.setState({ searchValue }, this.scrollToFactsheet)
    if (searchValue.length >= this.MIN_SEARCH_TEXT_LENGTH) {
      this.updateFilteredComponents(searchValue)
    }
  }

  // scrolls the factsheet into view without running a search, then clears the
  // request so it happens once.
  applyPendingFactsheetScroll = () => {
    if (!this.props.pendingFactsheetScroll) {
      return
    }
    this.props.clearPendingFactsheetScroll()
    this.scrollToFactsheet()
  }

  scrollToFactsheet = () => {
    // defer to the next frame so the header is in the DOM even when this runs
    // right after mount (the group buttons render on a follow-up setState).
    requestAnimationFrame(() => {
      this.factsheetHeaderRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }

  // jumps straight to a fact requested from outside the factsheet (e.g. clicking
  // a contradicting fact in the notification center): selects the fact's group,
  // then scrolls to and highlights it. clears the request so it runs once.
  applyPendingFactFocus = () => {
    const factId = this.props.pendingFactFocus
    if (!factId) {
      return
    }
    this.props.clearPendingFactFocus()
    // view changes are blocked while the factsheet has erroneous facts — show
    // the navigation-not-allowed notice, the same as manual group navigation.
    if (!this.props.factsheetIsValid) {
      this.setState({ showGroupNavigationNotAllowedModal: true })
      return
    }
    const groupKey = this.findGroupKeyForFact(factId)
    if (groupKey === null) {
      // the fact isn't placed in any group — fall back to the label search so
      // the user still finds it (and just scroll if we can't resolve a label).
      const label = this.props.factsheetBlueprint.factTree.facts[factId]?.label
      label ? this.runFactSearch(label) : this.scrollToFactsheet()
      return
    }
    // leave search mode and switch to the fact's group, then scroll to it.
    this.setState(
      { selectedGroupKey: groupKey, searchValue: '', showSelectedFactsOnly: false },
      () => this.scrollToFact(factId)
    )
  }

  // finds the top-level section:group component index whose subtree contains the
  // given fact def-id, or null when none does.
  findGroupKeyForFact = (factId) => {
    const components = this.props.blueprint.components
    for (let i = 0; i < components.length; i++) {
      if (components[i].type !== 'section:group') {
        continue
      }
      if (this.componentsContainFact(components[i].components ?? [], factId)) {
        return i
      }
    }
    return null
  }

  componentsContainFact = (components, factId) => components.some((component) => {
    if (this.SEARCHABLE_FACT_TYPES.has(component.type) && String(component.id) === String(factId)) {
      return true
    }
    return this.componentsContainFact(component.components ?? [], factId)
  })

  scrollToFact = (factId) => {
    // defer to the next frame so the newly-selected group's facts are in the DOM.
    requestAnimationFrame(() => {
      // scope the lookup to the selected group's container so we never match a
      // duplicate of the same fact rendered elsewhere (e.g. in search results).
      const element = this.factsContainerRef.current?.querySelector(`[data-fact-id="${factId}"]`)
      if (!element) {
        this.scrollToFactsheet()
        return
      }
      // pulse only once the fact has scrolled into view (see below).
      this.pulseFactWhenScrollSettles(element)
      element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    })
  }

  // adds the highlight pulse once the smooth scroll to a focused fact settles,
  // so the animation plays when the fact is in view rather than during the
  // scroll. mirrors the scroll-end detection used by fact-checkbox. runs once:
  // whichever of the scroll-settle or the already-in-view fallback fires first.
  factFocusScrollHandler = null
  factFocusSettleTimeout = null
  factFocusFallbackTimeout = null
  factFocusAnimationTimeout = null
  pulseFactWhenScrollSettles = (element) => {
    const highlightClass = 'factsheet-fact-highlight'
    this.cleanupFactFocus()
    const pulse = () => {
      this.cleanupFactFocus()
      element.classList.add(highlightClass)
      this.factFocusAnimationTimeout = setTimeout(
        () => element.classList.remove(highlightClass),
        500
      )
    }
    this.factFocusScrollHandler = () => {
      clearTimeout(this.factFocusSettleTimeout)
      this.factFocusSettleTimeout = setTimeout(pulse, 120)
    }
    window.addEventListener('scroll', this.factFocusScrollHandler)
    // the fact may already be in view, so no scroll fires — pulse anyway.
    this.factFocusFallbackTimeout = setTimeout(pulse, 600)
  }

  cleanupFactFocus = () => {
    if (this.factFocusScrollHandler) {
      window.removeEventListener('scroll', this.factFocusScrollHandler)
      this.factFocusScrollHandler = null
    }
    clearTimeout(this.factFocusSettleTimeout)
    clearTimeout(this.factFocusFallbackTimeout)
  }

  componentWillUnmount () {
    clearTimeout(this.toggleSelectedFactsOnlyTimer)
    this.cleanupFactFocus()
    clearTimeout(this.factFocusAnimationTimeout)
  }

  onGroupNavigationNotAllowedModal = (e) => {
    e.preventDefault()
    e.stopPropagation()
    this.setState({ showGroupNavigationNotAllowedModal: false })
  }

  onBlueprintChange () {
    let hasGroupedComponents = false
    const groupComponentKeys = {}
    const unmappedGroupKey = Math.max(...this.SECTION_GROUP_GROUPS.values()) + 1
    const newRenderGroups = new Map()
    for (let i = 0; i < this.props.blueprint.components.length; i++) {
      let component = this.props.blueprint.components[i]
      if (component.type !== 'section:group') {
        continue
      }
      groupComponentKeys[i] = 1
      hasGroupedComponents = true
      if (this.SECTION_GROUP_GROUPS.has(component.id)) {
        const renderGroup = this.SECTION_GROUP_GROUPS.get(component.id)
        if (!newRenderGroups.has(renderGroup)) {
          newRenderGroups.set(renderGroup, [])
        }
        newRenderGroups.get(renderGroup).push(i)
        continue
      }
      if (!newRenderGroups.has(unmappedGroupKey)) {
        newRenderGroups.set(unmappedGroupKey, [])
      }
      newRenderGroups.get(unmappedGroupKey).push(i)
    }
    this.setState({
      groupComponentKeys: groupComponentKeys,
      selectedGroupKey: hasGroupedComponents ? 0 : null,
      hasGroupedComponents: hasGroupedComponents,
      filteredComponents: [],
      renderGroups: newRenderGroups,
      renderGroupsKeys: [...newRenderGroups.keys()].sort((a, b) => a - b)
    })
  }

  onGroupClick (key, label) {
    activityTrackingService.trackEvent(trackingEvent.clickFactsheetCategory, { field: label })
    if (this.state.selectedGroupKey !== key && !this.props.factsheetIsValid) {
      this.setState({ showGroupNavigationNotAllowedModal: true })
      return
    }
    this.setState({ selectedGroupKey: key })
  }

  hasFactInComponents = (components) => {
    return components.some(component => {
      if (this.SEARCHABLE_FACT_TYPES.has(component.type)) {
        return true
      }
      return this.hasFactInComponents(component.components ?? [])
    })
  }

  componentsFiltered = (components, searchVal) => {
    return components.map((component) => {
      if (component.type === 'section:separator') {
        return null
      }
      if (this.SEARCHABLE_FACT_TYPES.has(component.type)) {
        const fact = this.props.blueprint.factTree.facts[component.id]
        if (searchVal.length > 0 && !fact.label.toLowerCase().includes(searchVal)) {
          return null
        }
        if (this.state.showSelectedFactsOnly && this.props.factsheetFacts[component.id] === undefined) {
          return null
        }
      }
      const newComponents = component.components
        ? this.componentsFiltered(component.components, searchVal)
        : []
      if (component.type.startsWith('section:box') || component.type.startsWith('section:group')) {
        if (!this.hasFactInComponents(newComponents)) {
          return null
        }
      }
      return {
        ...component,
        components: newComponents
      }
    }).filter(e => e !== null)
  }

  updateFilteredComponents = debounce((value) => {
    // const startTime = performance.now()
    const filteredComponents = this.componentsFiltered(this.props.blueprint.components, value.toLowerCase())
    // const endTime = performance.now()
    // console.log('SectionGroupContainer.updateFilteredComponents took', endTime - startTime, 'ms')
    this.setState({ filteredComponents, filteringInProgress: false })
  }, 100, {
    leading: false
  })
  trackSearchText = debounce((value) => {
    activityTrackingService.trackEvent(trackingEvent.searchFacts, {
      searchText: value
    })
  }, 500, {
    leading: false,
  })

  toggleSelectedFactsOnlyTimer = null
  toggleSelectedFactsOnly = (checked) => {
    const eventName = checked
      ? trackingEvent.enableDisplaySelectedFactsOnly
      : trackingEvent.disableDisplaySelectedFactsOnly
    activityTrackingService.trackEvent(eventName)
    if (!this.props.factsheetIsValid) {
      this.setState({ showGroupNavigationNotAllowedModal: true })
      return
    }
    this.setState({ showSelectedFactsOnly: checked, filteringInProgress: true }, () => {
      this.toggleSelectedFactsOnlyTimer && clearTimeout(this.toggleSelectedFactsOnlyTimer)
      if (!checked && this.state.searchValue.length < 1) {
        return
      }
      this.toggleSelectedFactsOnlyTimer = setTimeout(
        this.updateFilteredComponents.bind(this, this.state.searchValue),
        700
      )
    })
  }

  render () {
    if (!this.state.hasGroupedComponents) {
      return null
    }
    const searchBox = <SearchField
      onInput={(e) => {
        if (!this.props.factsheetIsValid) {
          this.setState({ showGroupNavigationNotAllowedModal: true })
          return
        }
        const searchValue = e.target.value.trimStart()
        this.setState({ searchValue })
        if (searchValue.length < this.MIN_SEARCH_TEXT_LENGTH) {
          return
        }
        this.updateFilteredComponents(searchValue)
        this.trackSearchText(searchValue)
      }}
      onClear={() => {
        this.setState({ searchValue: '' })
      }}
      value={this.state.searchValue}
      placeholderText={this.props.t('FACTS_SEARCH_PLACEHOLDER')}
    />
    const isSearchResultsMode = this.state.searchValue.length >= this.MIN_SEARCH_TEXT_LENGTH
      || this.state.showSelectedFactsOnly
    const { t } = this.props

    return (
      <React.Fragment>
        <div className="factsheet-header" ref={this.factsheetHeaderRef}>
          <PageHead
            marginBottom={0}
            title={t('FACTSHEET')}
            subtitle={t('FACTSHEET_SUB_HEADER')}
          />
          {searchBox}
        </div>
        <div className="factsheet-controls">
          <div className="factsheet-controls-toggle-option">
            <label className="fact-toggle-selected-facts-only" htmlFor="display-selected-facts-only">
              <Trans i18nKey="editProperty.factsheet.displayOptions.selectedFactsOnlyToggle.label"/>
            </label>
            <Toggle
              inputId="display-selected-facts-only"
              checked={this.state.showSelectedFactsOnly}
              onChange={this.toggleSelectedFactsOnly}
            />
          </div>
        </div>
        {
          !isSearchResultsMode && <>
            <div className="section-group-btns">
              <div className="section-group-btns__container">
                {this.state.renderGroupsKeys.map(renderGroupKey => {
                  return <div key={renderGroupKey} className="section-group-btns__group">
                    {this.state.renderGroups.get(renderGroupKey).map(groupCompKey => {
                      const sectionId = this.props.blueprint.components[groupCompKey].id
                      const section = this.props.factsheetBlueprint.factTree.sections[sectionId]
                      const label = getComponentLabel(
                        this.props.blueprint,
                        section)
                      const className = 'section-group-btns__button '
                        + (this.state.selectedGroupKey === groupCompKey ? 'section-group-btns__button--active' : '')
                      return <div
                        className={className}
                        key={groupCompKey}
                        onClick={() => this.onGroupClick(groupCompKey, label)}
                      >
                        {label}
                      </div>
                    })}
                  </div>
                })}
              </div>
            </div>
            <br/>
            <div className="facts-container" ref={this.factsContainerRef}>
              {this.props.blueprint.components[this.state.selectedGroupKey].components.map((component, index) => {
                const keyUsed = component && component.id ? `${component.type}:${component.id}` : `${component.type}:i${index}`
                if (environment.IS_DEV && window.__LOG_FS_KEYS) {
                  // Monitor key usage for undefined ids
                  if (!component || !component.id) {
                    console.warn('Factsheet key fallback used in SectionGroupContainer (main list)', {
                      parentId: this.props.blueprint.id,
                      index,
                      component
                    })
                  }
                  console.debug('Factsheet key used (SectionGroupContainer main):', keyUsed)
                }
                return <ComponentLoader
                  key={keyUsed}
                  blueprint={component} parentBlueprint={this.props.blueprint}
                  factsheetBlueprint={this.props.factsheetBlueprint}
                />
              })}
            </div>
          </>
        }
        {
          isSearchResultsMode && <div>
            {this.getSearchResults()}
          </div>
        }
        {
          this.state.showGroupNavigationNotAllowedModal && <ErroneousFactsNoticeOverlay
            onHide={this.onGroupNavigationNotAllowedModal}
            factLabels={this.props.erroneousFactLabelsMap.values().toArray()}
          />
        }
      </React.Fragment>
    )
  }

  getSearchResults = () => {
    if (this.state.filteringInProgress) {
      return <div>
        <h3 style={{ textAlign: 'center' }}>{this.props.t('editProperty.factsheet.searchResults.loadingNotice')}</h3>
      </div>
    }
    if (this.state.filteredComponents.length === 0) {
      return <div>
        <h3
          style={{ textAlign: 'center' }}
        >
          {
            this.state.searchValue.length > 0
              ? this.props.t('FACTS_SEARCH_NO_RESULTS', { term: this.state.searchValue })
              : this.props.t('editProperty.factsheet.searchResults.noSelectedFactsNotice')
          }
        </h3>
      </div>
    }
    return this.state.filteredComponents.map(component => {
      return component.components
        .map((component, index) => {
          const keyUsed = component && component.id ? `${component.type}:${component.id}` : `${component.type}:search:${index}`
          if (environment.IS_DEV && window.__LOG_FS_KEYS) {
            if (!component || !component.id) {
              console.warn('Factsheet key fallback used in SectionGroupContainer (search results)', {
                parentId: this.props.blueprint.id,
                index,
                component
              })
            }
            console.debug('Factsheet key used (SectionGroupContainer search):', keyUsed)
          }
          return <div key={`cont-${keyUsed}`} className="facts-container">
            <ComponentLoader
              key={keyUsed}
              blueprint={component} parentBlueprint={this.props.blueprint}
              factsheetBlueprint={this.props.factsheetBlueprint}
            />
          </div>
        })
    })
  }
}

const mapStateToProps = state => {
  return {
    factsheetIsValid: state.factsheet.validation.isValid,
    factsheetFacts: state.factsheet.facts,
    erroneousFactLabelsMap: state.factsheet.validation.erroneousFactLabelsMap,
    pendingFactsheetScroll: state.factsheet.pendingFactsheetScroll,
    pendingFactFocus: state.factsheet.pendingFactFocus,
  }
}

const mapDispatchToProps = {
  clearPendingFactsheetScroll: factsheetActions.clearPendingFactsheetScroll,
  clearPendingFactFocus: factsheetActions.clearPendingFactFocus,
}
SectionGroupContainer.propTypes = {
  blueprint: PropTypes.shape({ components: PropTypes.array.isRequired }).isRequired,
  factsheetBlueprint: PropTypes.shape(SHAPE_FACTSHEET_BLUEPRINT).isRequired
}
export default withTranslation()(connect(
  mapStateToProps,
  mapDispatchToProps,
)(SectionGroupContainer))
